import { useEffect, useMemo, useState } from 'react'
import { loadStripe } from '@stripe/stripe-js'
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js'
import { useStripeTheme, paymentElementAppearance } from '../../lib/stripeTheme'

const PK = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY

let _stripePromise = null
function getStripe() {
  if (!PK) return null
  if (!_stripePromise) _stripePromise = loadStripe(PK)
  return _stripePromise
}

function PaymentForm({ finalAmountCents, onSuccess, onClose, forcedMethod, prefill }) {
  const stripe = useStripe()
  const elements = useElements()
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [ready, setReady] = useState(false)

  const isCardVerify = finalAmountCents === 100
  const methodLabel =
    forcedMethod === 'us_bank_account'
      ? 'bank account'
      : forcedMethod === 'card'
        ? 'credit card'
        : null

  const handleSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault()
    if (e?.stopPropagation) e.stopPropagation()
    if (!stripe || !elements) return
    setBusy(true)
    setErr('')
    const result = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}${window.location.pathname}`,
      },
      redirect: 'if_required',
    })
    if (result.error) {
      setErr(result.error.message || 'Payment failed')
      setBusy(false)
      return
    }
    const piStatus = result.paymentIntent?.status
    if (piStatus === 'succeeded' || piStatus === 'processing') {
      if (onSuccess) onSuccess(result.paymentIntent)
      onClose()
    } else {
      setErr('Payment did not complete. Please try again.')
      setBusy(false)
    }
  }

  const submitLabel = busy
    ? 'Processing…'
    : isCardVerify
      ? 'Save card'
      : 'Pay now'

  return (
    <div role="form" aria-label="Stripe payment form">
      <div className="stripe-modal-warning">
        {isCardVerify ? (
          <>
            <strong>
              A $1 USD charge will be made to your card for verification and
              saved for future payments.
            </strong>{' '}
            It is refunded automatically within 3–5 business days.
          </>
        ) : (
          <>
            <strong>
              ${(finalAmountCents / 100).toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}{' '}
              charge{methodLabel ? ` via ${methodLabel}` : ''}.
            </strong>{' '}
            Network+ setup fee, billed once.
          </>
        )}
      </div>

      {!ready && (
        <div className="stripe-modal-loading">Loading payment form…</div>
      )}

      <div className="stripe-modal-element-wrap">
        <PaymentElement
          onReady={() => setReady(true)}
          options={{
            // No Apple/Google Pay — those OS/Stripe-hosted sheets can't be
            // themed and leak a white panel. Keep to card (+ bank when forced).
            wallets: { applePay: 'never', googlePay: 'never' },
            ...(forcedMethod ? { paymentMethodOrder: [forcedMethod] } : {}),
            defaultValues: {
              billingDetails: {
                email: prefill?.email || '',
                name: prefill?.name || '',
              },
            },
          }}
        />
      </div>

      {err && <div className="stripe-modal-error">{err}</div>}

      <div className="stripe-modal-footer">
        <button
          type="button"
          className="stripe-modal-cancel"
          onClick={onClose}
          disabled={busy}
        >
          Cancel
        </button>
        <button
          type="button"
          className="stripe-modal-submit"
          onClick={handleSubmit}
          disabled={busy || !stripe || !ready}
        >
          {submitLabel}
        </button>
      </div>

      <div className="stripe-modal-lockup">
        <span>Powered by</span>
        <img src="/stripe-logo.png" alt="Stripe" />
      </div>
    </div>
  )
}

export default function StripePaymentModal({
  clientSecret,
  finalAmountCents,
  onSuccess,
  onClose,
  forcedMethod,
  prefill,
}) {
  const stripePromise = useMemo(() => getStripe(), [])
  const theme = useStripeTheme()

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  if (!clientSecret) return null
  if (!stripePromise) {
    return (
      <div className="stripe-modal-backdrop" onClick={onClose}>
        <div className="stripe-modal" onClick={(e) => e.stopPropagation()}>
          <button
            className="stripe-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
          <div className="stripe-modal-title">Payment unavailable</div>
          <p className="stripe-modal-warning">
            Stripe publishable key is not configured. Set{' '}
            <code>VITE_STRIPE_PUBLISHABLE_KEY</code> in .env.
          </p>
        </div>
      </div>
    )
  }

  const isCardVerify = finalAmountCents === 100
  const title = isCardVerify ? 'Add card for verification' : 'Complete payment'

  return (
    <div className="stripe-modal-backdrop" onClick={onClose}>
      <div
        className="stripe-modal stripe-modal-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="stripe-modal-close"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>
        <div className="stripe-modal-title">{title}</div>
        <Elements
          key={theme}
          stripe={stripePromise}
          options={{
            clientSecret,
            appearance: paymentElementAppearance(theme),
          }}
        >
          <PaymentForm
            finalAmountCents={finalAmountCents}
            onSuccess={onSuccess}
            onClose={onClose}
            forcedMethod={forcedMethod}
            prefill={prefill}
          />
        </Elements>
      </div>
    </div>
  )
}